import { TCompany } from "@/api/entities/company.entity";
import { TMatch } from "@/api/entities/matches.entity";
import { useMe } from "@/api/services/main/auth";
import { TGetMeDto } from "@/api/services/main/auth/dtos/get-me.dto";
import { FCC } from "@/types/common";
import { createContext, MouseEventHandler, useContext, useMemo } from "react";
import { matchSchema } from "@/features/match/schema/matchSchema";
import { useVisible } from "@/hooks/useVisible";
import { TUserType } from "@/types/enum";
import { LoadingScreen } from "@/components/elements/Loading/Loading";

export type InitSetupType = {
  currentStep: number;
  checkFinishInitStep: MouseEventHandler<HTMLElement>;
  isLoading: boolean;
  userType?: TUserType;
  user?: TGetMeDto;
  match?: TMatch;
  company?: TCompany;
};

export const InitSetupContext = createContext<InitSetupType | undefined>(undefined);

export const InitSetupProvider: FCC = ({ children }) => {
  const matchInfoStep = useVisible();

  // Fetch user data from /get-me API
  const { data: meResult, isLoading } = useMe();

  const user = useMemo(() => meResult?.data, [meResult]);

  const userType: TUserType | undefined = user?.type;

  const match = useMemo(() => user?.match, [user]);
  const company = useMemo(() => user?.company, [user]);

  const isPassMatchInfoStep = useMemo(() => {
    // Company users and support users don't need to pass match info step
    if (userType === "company_user" || userType === "support_user") return true;

    if (!match) return false;
    const compareKeys: (keyof TMatch)[] = [];
    Object.entries(matchSchema.fields).forEach(([key, value]) => {
      const fieldDes = value.describe();
      if ("optional" in fieldDes && fieldDes.optional === false && "nullable" in fieldDes && fieldDes.nullable === false) {
        compareKeys.push(key as keyof TMatch);
      }
    });
    const isFullFillRso = !compareKeys.some(
      (key) => match[key] === null || match[key] === undefined || match[key] === "",
    );

    return isFullFillRso;
  }, [match, userType]);

  const currentStep = useMemo(() => {
    if (!isPassMatchInfoStep) return 1;
    return 5;
  }, [isPassMatchInfoStep]);

  const checkFinishInitStep: MouseEventHandler<HTMLElement> = (e) => {
    if (!isPassMatchInfoStep) {
      e.preventDefault();
      matchInfoStep.open();
      return;
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <InitSetupContext.Provider
      value={{
        user,
        match,
        company,
        currentStep,
        checkFinishInitStep,
        isLoading,
        userType,
      }}
    >
      {children}
    </InitSetupContext.Provider>
  );
};

export const useInitSetup = (): InitSetupType => {
  const context = useContext(InitSetupContext);
  if (!context) {
    throw new Error("useInitSetup must be used within a InitSetupProvider");
  }
  return context;
};
